import { UserService } from 'src/app/angularJS/services/user/user.service';
import { CustomerService } from 'src/app/angularJS/services/customer/customer.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';

export class ChargeAccountBaseComponent {

    constructor(
        public chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount,
        public breadcrumbService: BreadcrumbService,
        public customerService: CustomerService,
        public ACTToolbarService: ACTToolbarService,
        public userService: UserService,
        public $state: ng.ui.IStateService,
        public $translate: ng.translate.ITranslateService) { }

    /**
     * Initialises the primary toolbar's title, tabs, and breadcrumbs for the charge account screen.
     * 
     * @param sref a reference to the component to be associated with the final breadcrumb, e.g. 'charge-account-summary'.
     */
    public setToolbarAndBreadcrumbs(sref: string): void {
        const customerId = this.chargeAccount.customer ? this.chargeAccount.customer.link.split('/').pop() : undefined;
        if (customerId) {
            this.customerService.getCustomer(customerId).then((customer) => {
                const customerName: string = this.getFullCustomerName(customer);
                this.setBreadcrumbs(customerName, sref);
                this.setToolbar(customerName);
            });
        } else {
            this.setBreadcrumbs('ANONYMOUS_CHARGE_ACCOUNT', sref);
            this.setToolbar('ANONYMOUS_CHARGE_ACCOUNT');
        }
    }

    /**
     * Sets the primary toolbar's title and tabs for the charge account screen. If the user does not have the correct role for a tab, then the tab will not be displayed.
     * 
     * @param customerName the name of the customer to set as the toolbar title. For anonymous accounts, this should be set to 'ANONYMOUS_CHARGE_ACCOUNT'
     */
    public setToolbar(customerName: string) {
        this.ACTToolbarService.getToolbar('primary').then(toolbar => {
            toolbar.setTitle(customerName);

            const tabsToDisplay = [];

            const allTabPermissions: string[] = [
                'charge-account-tabs-summary',
                'charge-account-tabs-usage',
                'charge-account-tabs-payments',
                'charge-account-tabs-products',
                'charge-account-tabs-statement'
            ];

            const userTabPermissions: string[] = this.userService.permissions.ui
                ? Object.keys(this.userService.permissions.ui).filter((perm: string) =>
                    perm.toLowerCase().indexOf('charge-account-tabs-') !== -1
                    && this.userService.permissions.ui[perm].canAccess)
                : [];

            allTabPermissions.forEach((tabPermission: string) => {
                if (userTabPermissions.indexOf(tabPermission) !== -1) {
                    const tabName: string = tabPermission.replace('charge-account-tabs-', '');

                    const sRef: string = 'usage' === tabName
                        ? 'charge-account-journeys' + '({ chargeAccountId: \'' + this.chargeAccount.id + '\'})'
                        : 'charge-account-' + tabName.toLowerCase() + '({ chargeAccountId: \'' + this.chargeAccount.id + '\'})'

                    tabsToDisplay.push({
                        label: `CHARGE_ACCOUNT_TABS.${tabName.toUpperCase()}`,
                        sref: sRef
                    });
                }
            });

            const currentTab = this.$state.current.data && this.$state.current.data.selectedTab !== undefined ? this.$state.current.data.selectedTab : 0;

            toolbar.setTabs(tabsToDisplay, currentTab);
        });
    }

    /**
     * Sets the breadcrumbs for the charge account screen.
     *
     * @param customerName the name of the customer to set as the final breadcrumb. For anonymous accounts, this should be set to 'ANONYMOUS_CHARGE_ACCOUNT'
     * @param sref a reference to the component to render when the final breadcrumb is selected, e.g. 'charge-account-summary'
     */
    public setBreadcrumbs(customerName: string, sref: string): void {
        this.breadcrumbService.setBreadcrumbs([{
            name: 'ACCOUNT_SEARCH',
            sref: 'customers',
        }, {
            name: customerName,
            sref: sref,
            params: {
                chargeAccountId: this.chargeAccount.id
            }
        }]);
    }

    /**
     * Gets the full name of a customer, including title, forename, and surname (dependent upon what fields are present on the customer).
     *
     * @param customer the customer whose name is to be returned
     */
    public getFullCustomerName(customer: InterACT.Interfaces.Customers.Customer): string {
        let customerFullName;

        if (customer.forename && customer.title && customer.surname) {
            const title = this.$translate.instant('CUSTOMER_DETAIL.' + customer.title.toUpperCase());
            customerFullName = `${title} ${customer.forename} ${customer.surname}`;
        } else if (customer.title && customer.surname && !customer.forename) {
            const title = this.$translate.instant('CUSTOMER_DETAIL.' + customer.title.toUpperCase());
            customerFullName = `${title} ${customer.surname}`;
        } else if (customer.forename && customer.surname && !customer.title) {
            customerFullName = `${customer.forename} ${customer.surname}`;
        } else if (customer.forename && !customer.surname) {
            customerFullName = customer.forename;
        } else {
            customerFullName = this.$translate.instant('CUSTOMER');
        }

        return customerFullName;
    }
}